// src/components/Error/CriticalErrorBanner.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faRedo, 
  faTimes, 
  faWifi, 
  faCreditCard,
  faServer,
  faUserLock
} from '@fortawesome/free-solid-svg-icons';
import styles from './CriticalErrorBanner.module.css';

const CriticalErrorBanner = ({ error, onRetry, onDismiss }) => {
  if (!error) return null;
  
  const getErrorConfig = (type) => {
    switch (type) {
      case 'network':
        return {
          icon: faWifi,
          title: 'Connection Issue',
          bgColor: 'var(--color-warning-light)',
          borderColor: 'var(--color-warning)',
          iconColor: 'var(--color-warning)'
        };
      case 'backend_down':
        return {
          icon: faServer,
          title: 'Service Unavailable',
          bgColor: 'var(--color-warning-light)',
          borderColor: 'var(--color-warning)',
          iconColor: 'var(--color-warning)'
        };
      case 'credits':
        return {
          icon: faCreditCard,
          title: 'API Credits',
          bgColor: 'var(--color-error-light)',
          borderColor: 'var(--color-error)',
          iconColor: 'var(--color-error)'
        };
      case 'auth':
        return {
          icon: faUserLock,
          title: 'Authentication',
          bgColor: 'var(--color-error-light)',
          borderColor: 'var(--color-error)',
          iconColor: 'var(--color-error)'
        };
      case 'ai_unavailable':
        return {
          icon: faExclamationTriangle,
          title: 'AI Service',
          bgColor: 'var(--color-warning-light)',
          borderColor: 'var(--color-warning)',
          iconColor: 'var(--color-warning)'
        };
      default:
        return {
          icon: faExclamationTriangle,
          title: 'Error',
          bgColor: 'var(--color-background-light)',
          borderColor: 'var(--color-border)',
          iconColor: 'var(--color-text-secondary)'
        };
    }
  };
  
  const config = getErrorConfig(error.type);
  
  return (
    <div 
      className={styles.errorBanner}
      style={{
        backgroundColor: config.bgColor,
        borderBottom: `2px solid ${config.borderColor}`
      }}
    >
      <div className={styles.bannerContent}>
        <div className={styles.bannerIcon} style={{ color: config.iconColor }}>
          <FontAwesomeIcon icon={config.icon} />
        </div>
        <div className={styles.bannerText}>
          <div className={styles.bannerTitle}>
            {config.title}
          </div>
          <div className={styles.bannerMessage}>
            {error.message}
          </div>
          {error.suggestion && (
            <div className={styles.bannerSuggestion}>
              {error.suggestion}
            </div>
          )}
        </div>
      </div>
      <div className={styles.bannerActions}>
        {onRetry && (
          <button 
            onClick={onRetry}
            className={styles.retryButton}
          >
            <FontAwesomeIcon icon={faRedo} />
            <span>Retry</span>
          </button>
        )}
        <button 
          onClick={onDismiss}
          className={styles.dismissButton}
        >
          <FontAwesomeIcon icon={faTimes} />
          <span>Dismiss</span>
        </button>
      </div>
    </div>
  );
};

CriticalErrorBanner.propTypes = {
  error: PropTypes.shape({
    type: PropTypes.oneOf(['network', 'backend_down', 'credits', 'auth', 'ai_unavailable', 'server']),
    message: PropTypes.string.isRequired,
    suggestion: PropTypes.string
  }),
  onRetry: PropTypes.func,
  onDismiss: PropTypes.func.isRequired
};

export default CriticalErrorBanner;